import { LegalLayout } from '../components/LegalLayout';
import { Lock } from 'lucide-react';

export default function PrivacyPage() {
  const sections = [
    {
      id: "data-controller",
      title: "Data Controller",
      content: (
        <>
          <p>
            Visainterviews.ai is a trading name of <strong>Cloud Lab Technologies Ltd</strong>, registered in England and Wales, with its registered office at <strong>4 Crown Place, Liverpool Street, London, EC2A 4BT</strong> ("Controller", "we", "us" or "our").
          </p>
          <p>This Privacy Policy applies to data processing when you:</p>
          <ul>
            <li>Visit our website (Website)</li>
            <li>Use our Visainterviews.ai platform (Platform)</li>
            <li>Engage with our services (Services)</li>
          </ul>
        </>
      )
    },
    {
      id: "contact",
      title: "Data Protection Contact",
      content: (
        <>
          <p>If you have questions about this policy or wish to exercise your rights (see Section 9), you can contact us at:</p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:privacy@visainterviews.ai" className="text-lime-600 dark:text-lime-400 hover:underline">privacy@visainterviews.ai</a></li>
            <li><strong>Address:</strong> 4 Crown Place, Liverpool Street, London, EC2A 4BT</li>
          </ul>
        </>
      )
    },
    {
      id: "scope",
      title: "Scope of Data Processing",
      content: (
        <>
          <p>
            We process personal data in compliance with the <strong>UK GDPR</strong>, <strong>Data Protection Act 2018</strong>, and other applicable laws. Personal data means information relating to an identified or identifiable natural person. Processing includes collecting, storing, transmitting, deleting, or otherwise using such data.
          </p>
        </>
      )
    },
    {
      id: "categories",
      title: "Categories of Data, Purpose and Legal Basis",
      content: (
        <>
          <h3>4.1 Visiting our Website</h3>
          <p>When you visit our Website for informational purposes, we automatically collect server log data to ensure stability and security:</p>
          <ul>
            <li>IP address</li>
            <li>Browser type/version and settings</li>
            <li>Operating system</li>
            <li>Referrer URL</li>
            <li>Pages viewed, date/time of access</li>
            <li>Session duration</li>
          </ul>
          <p><strong>Legal Basis:</strong> Legitimate interests (Article 6(1)(f) UK GDPR) – ensuring website functionality and security.</p>

          <h3>4.2 Creating an Account and Subscription</h3>
          <p>When you register on our Platform:</p>
          <ul>
            <li>First and last name</li>
            <li>Email address</li>
            <li>Company name</li>
          </ul>
          <p>For paid subscriptions:</p>
          <ul>
            <li>Payment details (processed securely via third-party providers)</li>
          </ul>
          <p><strong>Legal Basis:</strong> Performance of a contract (Article 6(1)(b) UK GDPR).</p>

          <h3>4.3 Using the Voice Assistant</h3>
          <p>When you use our AI Voice Assistant services, we may process:</p>
          <ul>
            <li>Caller ID and phone number</li>
            <li>Call audio recordings (if enabled)</li>
            <li>Transcriptions of calls</li>
            <li>Interaction logs and AI-generated responses</li>
          </ul>
          <p><strong>Purpose:</strong> Delivering, improving, and monitoring service quality.</p>
          <p><strong>Legal Basis:</strong> Performance of a contract (Art. 6(1)(b) UK GDPR) and legitimate interests (Art. 6(1)(f) UK GDPR – fraud prevention, service improvement).</p>
          
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-4">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Special Notes:</strong> Where calls contain personal data of third parties, you (as User) are responsible for ensuring lawful collection and consent. You must not use our service to process special category data (e.g., health information) unless you have explicit consent or another lawful basis under Art. 9 UK GDPR.
            </p>
          </div>

          <h3>4.4 Contact Form and Support</h3>
          <p>When you contact us via form, email, or chat:</p>
          <ul>
            <li>Name</li>
            <li>Email address</li>
            <li>Message content</li>
          </ul>
          <p><strong>Legal Basis:</strong> Legitimate interests (Art. 6(1)(f) UK GDPR) – responding to your enquiry.</p>

          <h3>4.5 Cookies and Analytics</h3>
          <p>We use:</p>
          <ul>
            <li><strong>Essential cookies</strong> – to enable site functionality</li>
            <li><strong>Analytics cookies</strong> – only with your consent via our cookie banner (e.g., Google Analytics)</li>
            <li><strong>Marketing cookies</strong> – for remarketing and tracking campaigns</li>
          </ul>
          <p>You can manage preferences via the cookie banner or your browser settings.</p>

          <h3>4.6 Third-Party Integrations</h3>
          <p>If you connect third-party accounts (e.g., Twilio, Meta WhatsApp, Google Calendar), we process data as necessary to integrate those services.</p>
          <p><strong>Legal Basis:</strong> Performance of a contract (Art. 6(1)(b) UK GDPR).</p>
        </>
      )
    },
    {
      id: "recipients",
      title: "Data Recipients",
      content: (
        <>
          <p>We may share your data with:</p>
          <ul>
            <li>Cloud hosting providers (UK/EU-based where possible)</li>
            <li>Payment processors (e.g., Stripe)</li>
            <li>Communication API providers (e.g., Twilio)</li>
            <li>Analytics providers (e.g., Google Analytics)</li>
            <li>Professional advisers (legal, accounting)</li>
          </ul>
          <p>Some providers act as our processors under Art. 28 UK GDPR; others are independent controllers.</p>
        </>
      )
    },
    {
      id: "transfers",
      title: "International Data Transfers",
      content: (
        <>
          <p>
            Some processing involves transfers outside the UK/EU (e.g., to the USA for Twilio or Google). Where we do so, we ensure appropriate safeguards such as:
          </p>
          <ul>
            <li>UK International Data Transfer Agreement (IDTA) or Addendum to EU SCCs</li>
            <li>Adequacy decisions (where applicable)</li>
          </ul>
        </>
      )
    },
    {
      id: "retention",
      title: "Data Retention",
      content: (
        <>
          <p>We keep your personal data only for as long as necessary for the purposes collected, or as required by law. Examples:</p>
          <ul>
            <li><strong>Account data</strong> – kept while account is active + 6 years after termination for legal recordkeeping</li>
            <li><strong>Call recordings</strong> – kept for the duration specified in your account settings (default: 30 days) unless deleted earlier</li>
            <li><strong>Support tickets</strong> – 2 years from closure</li>
          </ul>
        </>
      )
    },
    {
      id: "security",
      title: "Security Measures",
      content: (
        <>
          <p>We implement appropriate technical and organizational measures to protect personal data, including:</p>
          <ul>
            <li>Encryption in transit and at rest</li>
            <li>Access controls and MFA for admin accounts</li>
            <li>Secure data centers certified to ISO 27001 or equivalent</li>
          </ul>
        </>
      )
    },
    {
      id: "rights",
      title: "Your Rights under UK GDPR",
      content: (
        <>
          <p>You have the following rights:</p>
          <ul>
            <li><strong>Access</strong> – request a copy of data we hold about you</li>
            <li><strong>Rectification</strong> – correct inaccurate or incomplete data</li>
            <li><strong>Erasure</strong> – request deletion where legally possible</li>
            <li><strong>Restriction</strong> – request limited processing</li>
            <li><strong>Portability</strong> – receive data in a machine-readable format</li>
            <li><strong>Object</strong> – to processing based on legitimate interests or for direct marketing</li>
            <li><strong>Withdraw consent</strong> – where processing is based on consent</li>
          </ul>
          <p>
            To exercise rights, email <a href="mailto:privacy@visainterviews.ai" className="text-lime-600 dark:text-lime-400 hover:underline">privacy@visainterviews.ai</a>.
          </p>
          <p>
            If unsatisfied, you can complain to the <strong>Information Commissioner's Office (ICO)</strong>:{' '}
            <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className="text-lime-600 dark:text-lime-400 hover:underline">
              https://ico.org.uk/make-a-complaint/
            </a>
            <br />
            Tel: 0303 123 1113
          </p>
        </>
      )
    },
    {
      id: "automated",
      title: "Automated Decision-Making & Profiling",
      content: (
        <>
          <p>
            We do not make solely automated decisions producing legal or similarly significant effects. Our AI responses are generated automatically but do not constitute formal decisions without human review.
          </p>
        </>
      )
    },
    {
      id: "changes",
      title: "Changes to this Policy",
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in law or our practices. We will post the updated policy with a new "Effective Date" and notify you of material changes.
          </p>
        </>
      )
    }
  ];

  return (
    <LegalLayout 
      title="Privacy Policy"
      effectiveDate="1 August 2025"
      lastUpdated="31 August 2025"
      sections={sections}
      icon={Lock}
    />
  );
}
